import { useState } from 'react'
import Modal from '@mui/material/Modal'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import ToggleButton from '@mui/material/ToggleButton'
import { alpha } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import VideocamIcon from '@mui/icons-material/Videocam'
import ImageIcon from '@mui/icons-material/Image'
import { useTranslation } from 'react-i18next'
import ChannelVideoStream from './ChannelVideoStream'

const LastEventZoomInModal = ({
  open,
  onClose,
  image,
  plateNumber,
  imageLoading,
  channelId,
}) => {
  const [displayItem, setDisplayItem] = useState('image');
  return (
    <Modal
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(11, 11, 11, 0.66)',
            backdropFilter: 'blur(5px)',
          }
        },
      }}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: { 
          xs: 2, 
          md: 4 
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          width: (image || displayItem === 'stream') ? '100%' : 'auto',
          maxHeight: '100%',
          maxWidth: 'lg',
          gap: 2,
          minWidth: 0,
          minHeight: 0,
          outline: 'none',
        }}
      >
        <Box 
          sx={(theme) => ({ 
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minWidth: 0,
            minHeight: 120,
            flex: '0 1 auto',
            backgroundColor: '#0f0f0f',
            width: '100%',
            position: 'relative',
            border: '1px solid',
            borderColor: alpha(theme.palette.text.light.primary, 0.3),
            borderRadius: '16px',
            overflow: 'hidden',
          })}
        >
          {
            displayItem === 'image' && (
              image ? (
                <LastEventImage 
                  image={image}
                  imageLoading={imageLoading}
                  plateNumber={plateNumber}
                  onClose={onClose}
                />
              ) : (
                <NoImagePlaceholder/>
              )
            )
          }
          {
            (displayItem === 'stream') && (
              <ChannelVideoStream 
                channelId={channelId} 
              />
            )
          }
        </Box>
        {
          <DisplayItemSelector
            value={displayItem}
            onChange={setDisplayItem}
          />
        }
      </Box>
    </Modal>
  )
}

const displayItems = [
  {
    value: 'image',
    translationKey: 'last_event_image',
    icon: <ImageIcon fontSize="small" sx={{ mr: 1 }}/>
  },
  {
    value: 'stream',
    translationKey: 'video_stream',
    icon: <VideocamIcon fontSize="small" sx={{ mr: 1 }}/>
  }
]

const DisplayItemSelector = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  return (
    <ToggleButtonGroup
      color="primary"
      value={value}
      size="small"
      exclusive
      onChange={(e,v) => v && onChange(v)}
    >
      {
        displayItems.map((filter) => (
          <ToggleButton 
            value={filter.value}
            key={filter.value}
            sx={(theme) => ({
              textTransform: 'none',
              fontWeight: 600,
              color: theme.palette.text.light.secondary,
              fontSize: {
                xs: '0.75rem',
                sm: '0.875rem',
              },
              '&.Mui-selected': (theme) => ({
                color: theme.palette.text.light.primary,
                textTransform: 'none',
                border: '1px solid',
                borderColor: theme.palette.primary.main,
                // backgroundColor: theme.palette.primary.main,
                // color: 'white',
              })
            })}
          >
            { filter.icon }
            { t(filter.translationKey) }
          </ToggleButton>
        ))
      }
    </ToggleButtonGroup>
  )
}

const LastEventImage = ({
  image,
  imageLoading,
  plateNumber,
  onClose,
}) => {
  const { t } = useTranslation();
  return (
    <>
      <Box
        component="img"
        src={image}
        alt={t('last_event')}
        sx={(theme) => ({
          flex: '0 1 auto',
          width: '100%',
          height: 'auto',
          maxHeight: '75vh',
          objectFit: 'contain',
          display: 'block',
        })}
      />   
      <IconButton
        onClick={onClose}
        sx={(theme) => ({
          position: 'absolute',
          top: 10,
          right: 10,
          zIndex: 10,
          bgcolor: '#0F0F0F',
          border: '2px solid',
          borderColor: alpha(theme.palette.text.light.primary, 0.7),
          color: alpha(theme.palette.text.light.primary, 0.8),
          opacity: 0.8,
          '&:hover': { 
            bgcolor: '#0F0F0F',
            opacity: 1
          },
        })}
      >
        <CloseIcon color="inherit"/>
      </IconButton>
      {
        plateNumber && (
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              left: 14,
              zIndex: 10,
              px: 1.5,
              py: 0.5,
              borderRadius: 1.5,
              bgcolor: 'rgba(0,0,0,0.6)',
              color: '#fff',
              backdropFilter: 'blur(6px)'
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              { plateNumber }
            </Typography>
          </Box>
        )
      }
    </>
  )
}

const NoImagePlaceholder = () => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        maxWidth: '720px',
        gap: 1,
        aspectRatio: {
          xs: '16 / 9', 
         //  sm: '21 / 9' 
       },
      }}
    >
      <ImageIcon
        sx={{ fontSize: 48, opacity: 0.5, color: 'rgba(255,255,255,0.5)' }}
      />
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
        {t('no_image')}
      </Typography>
    </Box>
  ) 
}
  
export default LastEventZoomInModal;